import { useCallback, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Popover,
  PopoverSurface,
  PopoverTrigger,
  makeStyles,
  tokens,
  mergeClasses,
} from '@fluentui/react-components';
import { GridDotsRegular } from '@fluentui/react-icons';
import { veracityColors, isLightColor } from '../tokens';
import { useAccessibleModules } from './moduleCatalog';
import { useLauncherCategories } from '../../entities/admin-config';
import { rainOverlay } from '../../lib';

const useStyles = makeStyles({
  trigger: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    backgroundColor: 'transparent',
    border: 'none',
    color: 'inherit',
    cursor: 'pointer',
    borderRadius: '4px',
    ':hover': { backgroundColor: 'rgba(0,0,0,0.06)' },
  },
  triggerOnDark: {
    color: '#FFFFFF',
    ':hover': { backgroundColor: 'rgba(255,255,255,0.15)' },
  },
  panel: {
    padding: tokens.spacingVerticalM,
    width: '320px',
  },
  title: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: veracityColors.midnightCharcoal,
    marginBottom: tokens.spacingVerticalM,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: tokens.spacingVerticalS,
  },
  tile: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    textDecoration: 'none',
    color: veracityColors.midnightCharcoal,
    border: `1px solid ${veracityColors.firstLight}`,
    backgroundColor: '#FFFFFF',
    transitionProperty: 'background-color, transform',
    transitionDuration: '120ms',
    ':hover': {
      backgroundColor: veracityColors.firstLight,
    },
  },
  tileCurrent: {
    border: `1px solid ${veracityColors.veracityOrange}`,
    backgroundColor: veracityColors.firstLight,
    cursor: 'default',
  },
  iconCircle: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: '#FFFFFF',
    fontSize: '16px',
  },
  label: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
  },
  empty: {
    padding: tokens.spacingVerticalL,
    fontSize: tokens.fontSizeBase200,
    color: veracityColors.duskBlue,
    textAlign: 'center',
  },
  homeLink: {
    display: 'block',
    marginTop: tokens.spacingVerticalM,
    paddingTop: tokens.spacingVerticalS,
    borderTop: `1px solid ${veracityColors.firstLight}`,
    fontSize: tokens.fontSizeBase200,
    color: veracityColors.duskBlue,
    textDecoration: 'none',
    textAlign: 'center',
    ':hover': { color: veracityColors.veracityOrange },
  },
  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: tokens.spacingVerticalM,
  },
  rainTrigger: {
    width: '22px',
    height: '22px',
    padding: 0,
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    opacity: 0.35,
    transitionProperty: 'opacity, transform',
    transitionDuration: '140ms',
    ':hover': { opacity: 1, transform: 'rotate(-12deg) scale(1.15)' },
  },
  rainTriggerImg: {
    width: '100%',
    height: '100%',
    display: 'block',
  },
});

const RAIN_IMG_SRC = '/veracity-icon.svg';

interface ModuleSwitcherProps {
  /** Apply the on-dark color treatment (white icon for dark headers). */
  onDark?: boolean;
}

/**
 * Header control that lets the user jump between modules without
 * round-tripping through the launcher. Lists only modules the user has
 * access to. Highlights the module the user is currently in.
 */
export function ModuleSwitcher({ onDark }: ModuleSwitcherProps) {
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  // The catalog hook returns accessible live modules plus `soon` placeholders;
  // the compact switcher only navigates to live modules, so drop `soon`.
  const accessibleModules = useAccessibleModules()
    .filter(({ module }) => module.status === 'live')
    .map(({ module }) => module);

  // Admin module-color overrides, shared with the launcher and AppShell.
  const { moduleColors } = useLauncherCategories();

  const currentModule = accessibleModules.find((m) => pathname.startsWith(m.path));

  const handleRain = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    rainOverlay({ kind: 'image', src: RAIN_IMG_SRC });
  }, []);

  return (
    <Popover open={open} onOpenChange={(_, data) => setOpen(data.open)} positioning="below-end">
      <PopoverTrigger>
        <button
          type="button"
          className={mergeClasses(styles.trigger, onDark && styles.triggerOnDark)}
          title="Switch module"
          aria-label="Switch module"
        >
          <GridDotsRegular style={{ fontSize: '20px' }} />
        </button>
      </PopoverTrigger>
      <PopoverSurface className={styles.panel}>
        <div className={styles.panelHeader}>
          <div className={styles.title} style={{ marginBottom: 0 }}>Vera Modules</div>
          <button
            type="button"
            className={styles.rainTrigger}
            onClick={handleRain}
            title="✨"
            aria-label="Daybreak"
          >
            <img src={RAIN_IMG_SRC} alt="" className={styles.rainTriggerImg} />
          </button>
        </div>
        {accessibleModules.length === 0 ? (
          <div className={styles.empty}>
            No modules assigned. Ask a Vera admin to grant you a role.
          </div>
        ) : (
          <div className={styles.grid}>
            {accessibleModules.map((m) => {
              const Icon = m.icon;
              const isCurrent = currentModule?.id === m.id;
              return (
                <Link
                  key={m.id}
                  to={isCurrent ? '#' : m.path}
                  className={mergeClasses(styles.tile, isCurrent && styles.tileCurrent)}
                  onClick={(e) => {
                    if (isCurrent) e.preventDefault();
                    else setOpen(false);
                  }}
                >
                  <div
                    className={styles.iconCircle}
                    // Admin override wins over the locked accent, same rule as
                    // the launcher tile and the AppShell header. Foreground is
                    // derived by luminance rather than read from
                    // accentForeground, which describes only the code default.
                    style={(() => {
                      const accent = moduleColors?.[m.id] ?? m.accent;
                      return {
                        backgroundColor: accent,
                        color: isLightColor(accent) ? veracityColors.midnightCharcoal : '#FFFFFF',
                      };
                    })()}
                  >
                    <Icon />
                  </div>
                  <span className={styles.label}>{m.name}</span>
                </Link>
              );
            })}
          </div>
        )}
        <Link to="/" className={styles.homeLink} onClick={() => setOpen(false)}>
          Home
        </Link>
      </PopoverSurface>
    </Popover>
  );
}
